/**
 * Fills all application sections with dummy data via API calls.
 * Only used when VITE_TEST_MODE is enabled.
 */
import {
    putCampaignApplicationPatient,
    updateApplicationInsuranceInfoViaApplication,
    updateApplicationFinancialInfo,
    updateApplicationAgreements,
    createTeamMember,
} from '~/services/campaign';
import { apiUrl, authHeaders } from '~/services/auth';

// --- Shared random data ---

const MOVIE_CHARACTERS = [
    { first: 'Neo', last: 'Anderson', nick: 'The One' },
    { first: 'Trinity', last: 'Unknown', nick: 'Trin' },
    { first: 'Morpheus', last: 'Zion', nick: 'Captain' },
    { first: 'Agent', last: 'Smith', nick: 'Mr. Smith' },
    { first: 'Niobe', last: 'Zion', nick: '' },
    { first: 'Tank', last: 'Operator', nick: 'Tanky' },
    { first: 'Cypher', last: 'Reagan', nick: '' },
    { first: 'Luke', last: 'Skywalker', nick: '' },
    { first: 'Leia', last: 'Organa', nick: 'Princess' },
    { first: 'Han', last: 'Solo', nick: 'Scruffy' },
    { first: 'Frodo', last: 'Baggins', nick: '' },
    { first: 'Gandalf', last: 'Grey', nick: 'Mithrandir' },
    { first: 'Aragorn', last: 'Elessar', nick: 'Strider' },
    { first: 'Ellen', last: 'Ripley', nick: '' },
    { first: 'Sarah', last: 'Connor', nick: '' },
    { first: 'Marty', last: 'McFly', nick: '' },
    { first: 'Claude', last: 'Monet', nick: '' },
    { first: 'Frida', last: 'Kahlo', nick: '' },
    { first: 'Salvador', last: 'Dali', nick: '' },
    { first: 'Andy', last: 'Warhol', nick: '' },
];

const CITIES = [
    'Portland', 'Austin', 'Denver', 'Seattle', 'Boston',
    'Chicago', 'Nashville', 'Asheville', 'Savannah', 'Boise',
];

const STREETS = [
    '123 Main St', '456 Oak Ave', '789 Elm Blvd', '321 Pine Rd',
    '654 Maple Dr', '987 Cedar Ln', '111 Birch Way', '222 Walnut Ct',
];

const MIDDLE_NAMES = ['', 'James', 'Marie', 'Ray', 'Lee', 'Anne', 'Jo', 'Thomas'];

const US_STATES: Record<string, string> = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas',
    CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
    IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
    KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
    MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
    NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
    NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
    OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
    VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
    WI: 'Wisconsin', WY: 'Wyoming',
};

const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const randomPhone = () => {
    const d = () => Math.floor(Math.random() * 10);
    return `(${d()}${d()}${d()}) ${d()}${d()}${d()}-${d()}${d()}${d()}${d()}`;
};

const randomZip = () => String(Math.floor(10000 + Math.random() * 90000));

const randomDate = () => {
    const year = 1960 + Math.floor(Math.random() * 45);
    const month = String(1 + Math.floor(Math.random() * 12)).padStart(2, '0');
    const day = String(1 + Math.floor(Math.random() * 28)).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];

// --- Medical center search helpers ---

const searchMedicalCenters = async (state: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/v2/searchMedicalCenters`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ state }),
    });
    if (!resp.ok) throw new Error('Failed to search medical centers');
    return await resp.json();
};

const searchMedicalProfessionals = async (medCenterId: number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/v2/searchMedicalProfessionals`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ medCenterId }),
    });
    if (!resp.ok) throw new Error('Failed to search medical professionals');
    return await resp.json();
};

const setMedicalProfessionalViaApplication = async (
    applicationId: number,
    data: {
        medCenterId: number;
        personId: number;
        consent: boolean;
        certifyIdentity: number;
        signature: string;
    }
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignApplications/${applicationId}/medicalInfo`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
    });
    if (!resp.ok) throw new Error('Failed to set medical professional');
    return await resp.json();
};

// --- Section fill functions ---

type StepCallback = (step: string) => void;

async function fillClientInfo(applicationId: number, onStep?: StepCallback) {
    onStep?.('Filling client info...');
    const char = pick(MOVIE_CHARACTERS);
    const stateAbbr = pick(Object.keys(US_STATES));

    await putCampaignApplicationPatient(applicationId, {
        title: '',
        first_name: char.first,
        middle_name: pick(MIDDLE_NAMES),
        last_name: char.last,
        suffix: '',
        nicknames: char.nick,
        search_names: '',
        address_1: pick(STREETS),
        address_2: Math.random() > 0.5 ? `Apt ${Math.floor(Math.random() * 300) + 1}` : '',
        city: pick(CITIES),
        state: stateAbbr,
        zip_code: randomZip(),
        email: `alex+${char.first.toLowerCase()}${Date.now().toString(36)}@bowst.com`,
        phone_set: [{ phone: randomPhone(), type: 'Cell' }],
        birth_date: randomDate(),
        gender: pick(['Male', 'Female', 'Nonbinary']),
        marital_status: pick(['Single', 'Married', 'Divorced', 'Widowed']),
        preferred_language: 'English',
        pronouns: '',
        patient_is_deceased: false,
        death_date: null,
        ethnicity: pick(['White', 'Black or African American', 'Hispanic or Latino', 'Asian', 'Other']),
        admin: true,
    });
}

async function fillContactInfo(
    campaignId: number,
    metadata: any,
    onStep?: StepCallback
) {
    onStep?.('Filling primary contact...');
    const char = pick(MOVIE_CHARACTERS);
    const stateAbbr = pick(Object.keys(US_STATES));

    // Pick a relationship option from metadata.campaign_is_for
    const relationshipOptions = (metadata?.campaign_is_for || []).filter(
        (item: any) => item?.code !== 'self'
    );
    const relationship: any = relationshipOptions.length > 0
        ? pick(relationshipOptions)
        : null;

    await createTeamMember({
        campaign_id: campaignId,
        person: {
            first_name: char.first,
            middle_name: pick(MIDDLE_NAMES),
            last_name: char.last,
            suffix: '',
            title: '',
            address_1: pick(STREETS),
            address_2: '',
            city: pick(CITIES),
            state: stateAbbr,
            zip_code: randomZip(),
            email: `alex+${char.first.toLowerCase()}${Date.now().toString(36)}@bowst.com`,
            nicknames: '',
            phone_set: [{ phone: randomPhone(), type: 'Cell' }],
        },
        relationship_to_patient_id: relationship?.id || null,
        role: 'limited',
        is_primary_contact: true,
        is_emergency_contact: false,
        can_discuss_balance: false,
        can_request_disbursement: false,
        notes: '',
    });

    // Create a separate emergency contact
    onStep?.('Filling emergency contact...');
    const emergencyChar = pick(MOVIE_CHARACTERS.filter((c) => c !== char));
    const emergencyStateAbbr = pick(Object.keys(US_STATES));
    const emergencyRelationship: any = relationshipOptions.length > 0
        ? pick(relationshipOptions)
        : null;

    await createTeamMember({
        campaign_id: campaignId,
        person: {
            first_name: emergencyChar.first,
            middle_name: pick(MIDDLE_NAMES),
            last_name: emergencyChar.last,
            suffix: '',
            title: '',
            address_1: pick(STREETS),
            address_2: '',
            city: pick(CITIES),
            state: emergencyStateAbbr,
            zip_code: randomZip(),
            email: `alex+${emergencyChar.first.toLowerCase()}${Date.now().toString(36)}@bowst.com`,
            nicknames: '',
            phone_set: [{ phone: randomPhone(), type: 'Cell' }],
        },
        relationship_to_patient_id: emergencyRelationship?.id || null,
        role: 'no',
        is_primary_contact: false,
        is_emergency_contact: true,
        can_discuss_balance: false,
        can_request_disbursement: false,
        notes: '',
    });
}

async function fillMedicalInfo(applicationId: number, onStep?: StepCallback) {
    onStep?.('Filling medical professional info...');
    const char = pick(MOVIE_CHARACTERS);

    // Try a few states until we find one with medical centers and professionals
    const stateNames = Object.values(US_STATES);
    const shuffledStates = [...stateNames].sort(() => Math.random() - 0.5);

    for (const stateName of shuffledStates.slice(0, 10)) {
        try {
            const centers: any[] = await searchMedicalCenters(stateName);
            if (!centers || centers.length === 0) continue;

            const center: any = pick(centers);
            const professionals: any[] = await searchMedicalProfessionals(center.id);
            if (!professionals || professionals.length === 0) continue;

            const professional: any = pick(professionals);

            await setMedicalProfessionalViaApplication(applicationId, {
                medCenterId: center.id,
                personId: professional.id,
                consent: true,
                certifyIdentity: 1, // Client
                signature: `${char.first} ${char.last}`,
            });
            return;
        } catch {
            continue;
        }
    }

    throw new Error('Could not find a medical center with professionals in any state');
}

async function fillInsuranceInfo(applicationId: number, onStep?: StepCallback) {
    onStep?.('Filling insurance info...');
    const hasInsurance = Math.random() > 0.3;

    await updateApplicationInsuranceInfoViaApplication(applicationId, {
        ins_has_medicare: hasInsurance && Math.random() > 0.5,
        ins_has_medicaid: hasInsurance && Math.random() > 0.5,
        ins_has_private: hasInsurance && Math.random() > 0.7,
        ins_has_cobra: false,
        ins_has_none: !hasInsurance,
        ins_unknown: false,
        ins_comments: hasInsurance ? 'Coverage through employer' : 'No current coverage',
        ins_company: hasInsurance ? pick(['Blue Cross', 'Aetna', 'UnitedHealth', 'Cigna', 'Humana']) : '',
        ins_premium: hasInsurance ? Math.floor(Math.random() * 500) + 100 : null,
    });
}

async function fillFinancialInfo(campaignRequestId: number, onStep?: StepCallback) {
    onStep?.('Filling financial info...');
    const char = pick(MOVIE_CHARACTERS);
    const monthlyWages = Math.floor(Math.random() * 4000) + 1000;
    const ssSec = Math.random() > 0.5 ? Math.floor(Math.random() * 1500) : 0;
    const disability = Math.random() > 0.7 ? Math.floor(Math.random() * 1000) : 0;
    const otherIncome = Math.random() > 0.8 ? Math.floor(Math.random() * 500) : 0;
    const totalMonthly = monthlyWages + ssSec + disability + otherIncome;

    await updateApplicationFinancialInfo(campaignRequestId, {
        fin_num_in_household: Math.floor(Math.random() * 5) + 1,
        fin_is_single_parent_household: Math.random() > 0.7,
        fin_is_irs_dependent: Math.random() > 0.8,
        fin_monthly_wages: monthlyWages,
        fin_soc_sec_income: ssSec,
        fin_disability_income: disability,
        fin_other_income: otherIncome,
        fin_tot_monthly_income: totalMonthly,
        fin_anl_household_income: totalMonthly * 12,
        fin_has_hospital_costs: Math.random() > 0.3,
        fin_has_medication_costs: Math.random() > 0.3,
        fin_has_travel_costs: Math.random() > 0.5,
        fin_has_phys_therapy_costs: Math.random() > 0.6,
        fin_has_copay_costs: Math.random() > 0.4,
        fin_has_home_healthcare_costs: Math.random() > 0.7,
        fin_has_post_care_costs: Math.random() > 0.7,
        fin_has_living_donor_costs: Math.random() > 0.9,
        fin_has_donor_search_costs: Math.random() > 0.9,
        fin_has_med_equip_costs: Math.random() > 0.7,
        fin_has_home_modification_costs: Math.random() > 0.8,
        fin_has_transportation_costs: Math.random() > 0.5,
        fin_has_emergency_living_expenses: Math.random() > 0.6,
        fin_other_comments: '',
        fin_certify_identity: 1, // Client
        fin_signature: `${char.first} ${char.last}`,
        fin_signature_date: todayISO(),
    });
}

async function fillAgreements(applicationId: number, onStep?: StepCallback) {
    onStep?.('Filling agreements...');
    const char = pick(MOVIE_CHARACTERS);

    await updateApplicationAgreements(applicationId, {
        ag_certify_identity: 1, // Client
        ag_certify_info: true,
        ag_medpro_required: true,
        ag_terms_of_service: true,
        ag_understand_funds_come_to_hhl: true,
        ag_understand_hhl_may_use_photos: true,
        ag_understand_hhl_publishes_health_info: true,
        ag_understand_hhl_may_use_public_content: true,
        ag_signature: `${char.first} ${char.last}`,
        ag_signature_date: todayISO(),
        ag_terms_completed: true,
    });
}

// --- Main orchestrator ---

interface FillDummyDataParams {
    applicationId: number;
    campaignId: number;
    campaignRequestId: number;
    metadata: any;
    onStep?: StepCallback;
}

export async function fillDummyApplicationData({
    applicationId,
    campaignId,
    campaignRequestId,
    metadata,
    onStep,
}: FillDummyDataParams): Promise<{ success: boolean; errors: string[] }> {
    const errors: string[] = [];

    // Fill sections sequentially - order matters since some depend on client info existing
    const sections = [
        { name: 'Client Info', fn: () => fillClientInfo(applicationId, onStep) },
        { name: 'Contact Info', fn: () => fillContactInfo(campaignId, metadata, onStep) },
        { name: 'Medical Professional', fn: () => fillMedicalInfo(applicationId, onStep) },
        { name: 'Insurance', fn: () => fillInsuranceInfo(applicationId, onStep) },
        { name: 'Financial', fn: () => fillFinancialInfo(campaignRequestId, onStep) },
        { name: 'Agreements', fn: () => fillAgreements(applicationId, onStep) },
    ];

    for (const section of sections) {
        try {
            await section.fn();
        } catch (err: any) {
            console.error(`Error filling ${section.name}:`, err);
            errors.push(`${section.name}: ${err.message || 'Unknown error'}`);
        }
    }

    return { success: errors.length === 0, errors };
}
